import React, { useEffect, useState, useCallback, useRef, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import useIdleTimeout from '../hooks/useIdleTimeout';
import './CafeDashboard.css';

function pickupTimeToDate(pickupTime) {
  if (!pickupTime || !/^\d{2}:\d{2}$/.test(pickupTime)) return null;
  const [h, m] = pickupTime.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
}

function useOrderAge(createdAt, pickupTime) {
  const [age, setAge] = useState('');
  const [level, setLevel] = useState('green');
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    function update() {
      const pickupDate = pickupTimeToDate(pickupTime);
      const now = Date.now();

      if (pickupDate && pickupDate.getTime() > now) {
        const minsUntil = Math.ceil((pickupDate.getTime() - now) / 60000);
        if (minsUntil >= 60) {
          const h = Math.floor(minsUntil / 60);
          const m = minsUntil % 60;
          setCountdown(m > 0 ? `in ${h}h ${m}m` : `in ${h}h`);
        } else {
          setCountdown(`in ${minsUntil}m`);
        }
        setAge('');
        setLevel('green');
        return;
      }

      setCountdown('');
      const ref = pickupDate || new Date(createdAt);
      const mins = Math.floor((now - ref.getTime()) / 60000);
      if (mins < 1) { setAge('just now'); setLevel('green'); }
      else { setAge(`${mins}m`); setLevel(mins < 5 ? 'green' : mins < 10 ? 'amber' : 'red'); }
    }
    update();
    const id = setInterval(update, 15000);
    return () => clearInterval(id);
  }, [createdAt, pickupTime]);

  return { age, level, countdown };
}

function OrderAge({ createdAt, pickupTime }) {
  const { age, level, countdown } = useOrderAge(createdAt, pickupTime);
  if (countdown) return <span className="order-age order-age-countdown">{countdown}</span>;
  return <span className={`order-age order-age-${level}`}>{age}</span>;
}

function OrderCard({
  order,
  section,
  newOrderIds,
  advanceSectionStatus,
  setCancelTarget,
  actionLabel,
  actionClass,
  onReactivate,
  onReprintStickers,
  reprinting,
}) {
  const isActive = ['pending', 'preparing'].includes(order.sectionStatus);
  const { level, countdown } = useOrderAge(order.created_at, order.pickup_time);
  const isUrgent = isActive && level === 'red';
  const showTimer = isActive || countdown;
  const canReprint = ['pending', 'preparing', 'ready'].includes(order.sectionStatus);

  const isSundaySchool = Boolean(order.sunday_school_servant);
  const className = [
    'cafe-order card',
    `status-${order.sectionStatus}`,
    newOrderIds.has(order.id) ? 'cafe-order-new' : '',
    isUrgent ? 'cafe-order-urgent' : '',
    isSundaySchool ? 'cafe-order-sunday-school' : '',
  ].filter(Boolean).join(' ');

  return (
    <div key={`${order.id}-${section.name}`} className={className}>
      <div className="cafe-order-header">
        <span className={`badge badge-${order.sectionStatus}`}>{order.sectionStatus}</span>
        <div className="cafe-order-header-right">
          {isSundaySchool && (
            <span className="cafe-ss-badge" title="Sunday School servant — please prioritise">
              SUNDAY SCHOOL
            </span>
          )}
          {showTimer && <OrderAge createdAt={order.created_at} pickupTime={order.pickup_time} />}
        </div>
      </div>

      <div className="cafe-order-name">
        <span className="cafe-order-number">{order.order_number}</span>
        {order.customer_name && order.customer_name !== 'Guest' && (
          <span>
            {' · '}
            {order.customer_name}
            {isSundaySchool && <span className="cafe-ss-inline"> · SS</span>}
          </span>
        )}
      </div>

      {order.pickup_time && (
        <div className="cafe-order-pickup">Pickup: {(() => {
          const [h, m] = order.pickup_time.split(':').map(Number);
          const ampm = h >= 12 ? 'PM' : 'AM';
          return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
        })()}</div>
      )}

      <div className="cafe-order-items">
        {order.sectionItems.map((item) => {
          const opts = item.options ? (typeof item.options === 'string' ? JSON.parse(item.options) : item.options) : null;
          return (
            <div key={item.id} className="cafe-order-item">
              <span className={`cafe-item-qty${item.quantity > 1 ? ' cafe-item-qty-multi' : ''}`}>{item.quantity}x</span>
              <div className="cafe-item-detail">
                <span className="cafe-item-name">{item.item_name}</span>
                {opts && (
                  <span className="cafe-item-options">
                    {Object.entries(opts).filter(([, v]) => v).map(([k, v], i, arr) => (
                      <span key={k}>{k}: <strong>{v}</strong>{i < arr.length - 1 ? ' · ' : ''}</span>
                    ))}
                  </span>
                )}
                {item.item_note ? (
                  <span className="cafe-item-note">{item.item_note}</span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {order.notes && (
        <div className="cafe-order-notes">{order.notes}</div>
      )}

      {canReprint && (
        <div className="cafe-order-actions">
          <button
            className={`btn btn-sm ${actionClass[order.sectionStatus]} cafe-action-btn`}
            onClick={() => advanceSectionStatus(order, section.name)}
          >
            {actionLabel[order.sectionStatus]}
          </button>
          {order.sectionStatus === 'pending' && (
            <button
              className="btn btn-sm btn-danger cafe-action-btn"
              onClick={() => setCancelTarget(order)}
            >
              Cancel
            </button>
          )}
          {onReprintStickers && (
            <button
              type="button"
              className="btn btn-sm btn-outline cafe-action-btn cafe-reprint-btn"
              disabled={reprinting}
              onClick={() => onReprintStickers(order, section.name)}
              title={`Re-queue ${section.name} stickers for this order`}
            >
              {reprinting ? 'Queuing…' : `Reprint ${section.name}`}
            </button>
          )}
        </div>
      )}
      {['collected', 'cancelled'].includes(order.sectionStatus) && onReactivate && (
        <div className="cafe-order-actions">
          <button
            className="btn btn-sm btn-warning cafe-action-btn"
            onClick={() => onReactivate(order)}
          >
            Reactivate
          </button>
        </div>
      )}
    </div>
  );
}

const STATUS_FLOW = ['pending', 'preparing', 'ready', 'collected'];

const SECTION_ORDER = ['Hot Drinks', 'Cold Drinks', 'Cafe Food'];

const PRINT_ACTIONS = [
  { slice: 'all', label: 'Print all open orders' },
  { slice: 'Hot Drinks', label: 'Print all Hot Drinks' },
  { slice: 'Cold Drinks', label: 'Print all Cold Drinks' },
  { slice: 'Cafe Food', label: 'Print all Cafe Food' },
];

/** True when order has a pickup time more than 5 min away (local clock). Cancelled orders never scheduled. */
function isPickupLaterToday(order) {
  if (order.status === 'cancelled') return false;
  const pt = order.pickup_time;
  if (!pt || !/^\d{2}:\d{2}$/.test(pt)) return false;
  const [h, m] = pt.split(':').map(Number);
  const now = new Date();
  const pickupMins = h * 60 + m;
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const PREP_LEAD_MINS = 5;
  return nowMins < pickupMins - PREP_LEAD_MINS;
}

function countActiveScheduledOrders(sectionList) {
  const ids = new Set();
  for (const s of sectionList) {
    for (const o of s.orders) {
      if (o.status === 'cancelled') continue;
      if (['pending', 'preparing', 'ready'].includes(o.sectionStatus)) ids.add(o.id);
    }
  }
  return ids.size;
}

function playNotificationChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [523.25, 659.25, 783.99];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.15 + 0.4);
      osc.start(ctx.currentTime + i * 0.15);
      osc.stop(ctx.currentTime + i * 0.15 + 0.4);
    });
  } catch (e) {
    // Web Audio not available
  }
}

function deriveSectionStatus(sectionItems) {
  const statuses = sectionItems.map(i => i.status || 'pending');
  if (statuses.every(s => s === 'cancelled')) return 'cancelled';
  if (statuses.every(s => s === 'collected' || s === 'cancelled')) return 'collected';
  if (statuses.every(s => s === 'ready' || s === 'collected' || s === 'cancelled')) return 'ready';
  if (statuses.some(s => s === 'preparing' || s === 'ready' || s === 'collected')) return 'preparing';
  return 'pending';
}

function groupOrdersBySection(orders) {
  const sections = {};

  for (const order of orders) {
    const categories = new Set();
    for (const item of order.items) {
      categories.add(item.category_name || 'Other');
    }

    for (const cat of categories) {
      if (!sections[cat]) sections[cat] = [];
      const sectionItems = order.items.filter((it) => (it.category_name || 'Other') === cat);
      sections[cat].push({
        ...order,
        sectionItems,
        sectionStatus: order.status === 'cancelled' ? 'cancelled' : deriveSectionStatus(sectionItems),
      });
    }
  }

  const sorted = [];
  for (const name of SECTION_ORDER) {
    if (sections[name]) {
      sorted.push({ name, orders: sections[name] });
      delete sections[name];
    }
  }
  for (const [name, sectionOrders] of Object.entries(sections)) {
    sorted.push({ name, orders: sectionOrders });
  }

  return sorted;
}

export default function CafeDashboard() {
  const navigate = useNavigate();
  const idleWarning = useIdleTimeout('cafe');
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('active');
  const [loading, setLoading] = useState(true);
  const prevOrderCountRef = useRef(null);
  const prevOrderIdsRef = useRef(new Set());
  const [newOrderIds, setNewOrderIds] = useState(new Set());
  const [cancelTarget, setCancelTarget] = useState(null);
  const [toastMsg, setToastMsg] = useState(null);
  const [reprintingId, setReprintingId] = useState(null);
  const [printMenuOpen, setPrintMenuOpen] = useState(false);
  const [printPreview, setPrintPreview] = useState(null);
  const [printBusy, setPrintBusy] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearPin, setClearPin] = useState('');
  const [clearError, setClearError] = useState('');
  const [clearBusy, setClearBusy] = useState(false);
  const printMenuRef = useRef(null);
  const [selectedTabs, setSelectedTabs] = useState(() => {
    try {
      const stored = sessionStorage.getItem('cafe-tabs');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch { /* ignore */ }
    const legacy = sessionStorage.getItem('cafe-tab');
    return legacy ? [legacy] : ['All'];
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [timeTick, setTimeTick] = useState(0);
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);

  useEffect(() => {
    const id = setInterval(() => setTimeTick((t) => t + 1), 15000);
    return () => clearInterval(id);
  }, []);

  const fetchOrders = useCallback(() => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    api
      .getOrders({ date: today, venue: 'cafe' })
      .then((data) => {
        const activeCount = data.filter(
          (o) => o.status === 'pending' || o.status === 'preparing'
        ).length;
        if (prevOrderCountRef.current !== null && activeCount > prevOrderCountRef.current) {
          playNotificationChime();
          const currentIds = new Set(data.map((o) => o.id));
          const fresh = new Set();
          currentIds.forEach((id) => { if (!prevOrderIdsRef.current.has(id)) fresh.add(id); });
          if (fresh.size > 0) {
            setNewOrderIds(fresh);
            setTimeout(() => setNewOrderIds(new Set()), 4000);
          }
        }
        prevOrderCountRef.current = activeCount;
        prevOrderIdsRef.current = new Set(data.map((o) => o.id));
        setOrders(data);
      })
      .catch((err) => console.error('CafeDashboard fetch error:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 5000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  function showToast(msg) {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 4000);
  }

  async function advanceSectionStatus(order, sectionName) {
    const currentIdx = STATUS_FLOW.indexOf(order.sectionStatus);
    if (currentIdx < 0 || currentIdx >= STATUS_FLOW.length - 1) return;
    const nextStatus = STATUS_FLOW[currentIdx + 1];
    try {
      await api.updateOrderItemsStatus(order.id, nextStatus, sectionName);
      fetchOrders();
    } catch (err) {
      showToast(err.message);
    }
  }

  async function reactivateOrder(order) {
    try {
      await api.updateOrderStatus(order.id, 'pending');
      fetchOrders();
      showToast(`Order ${order.order_number} reactivated`);
    } catch (err) {
      showToast(err.message);
    }
  }

  async function confirmCancelOrder() {
    if (!cancelTarget) return;
    try {
      await api.updateOrderStatus(cancelTarget.id, 'cancelled');
      fetchOrders();
      showToast(`Order ${cancelTarget.order_number} cancelled`);
    } catch (err) {
      showToast(err.message);
    }
    setCancelTarget(null);
  }

  async function reprintStickers(order, sectionName) {
    if (reprintingId) return;
    const label = sectionName ? `${sectionName} stickers` : 'all stickers';
    const ok = window.confirm(
      `Reprint ${label} for order ${order.order_number}?`
    );
    if (!ok) return;
    setReprintingId(order.id);
    try {
      const result = await api.reprintCafeStickers(order.id, {
        category: sectionName || undefined,
      });
      const n = result.queued ?? result.total ?? 0;
      showToast(
        n > 0
          ? `Queued ${n} sticker${n === 1 ? '' : 's'} for ${order.order_number} (${sectionName || 'all'})`
          : `No stickers to print for ${order.order_number}`
      );
    } catch (err) {
      showToast(err.message || 'Reprint failed');
    } finally {
      setReprintingId(null);
    }
  }

  useEffect(() => {
    if (!printMenuOpen) return undefined;
    function onPointerDown(event) {
      if (printMenuRef.current && !printMenuRef.current.contains(event.target)) {
        setPrintMenuOpen(false);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [printMenuOpen]);

  async function choosePrintSlice(slice) {
    setPrintMenuOpen(false);
    setPrintBusy(true);
    try {
      const preview = await api.previewOpenCafeStickers(slice);
      if ((preview.stickers || 0) === 0 && (preview.replaced || 0) === 0) {
        showToast('No open cafe orders to print');
        return;
      }
      setPrintPreview(preview);
    } catch (err) {
      showToast(err.message || 'Could not prepare stickers');
    } finally {
      setPrintBusy(false);
    }
  }

  async function confirmPrint() {
    if (!printPreview || printBusy) return;
    setPrintBusy(true);
    try {
      const result = await api.printOpenCafeStickers(printPreview.slice);
      const n = result.queued || 0;
      const label = result.slice === 'all' ? 'open orders' : result.slice;
      showToast(
        n > 0
          ? `Queued ${n} sticker${n === 1 ? '' : 's'} for ${label}`
          : `Cleared waiting stickers for ${label}`
      );
      setPrintPreview(null);
    } catch (err) {
      showToast(err.message || 'Print failed');
    } finally {
      setPrintBusy(false);
    }
  }

  function openClearModal() {
    setClearPin('');
    setClearError('');
    setClearOpen(true);
  }

  async function confirmClearOpen(event) {
    event.preventDefault();
    if (clearBusy) return;
    setClearBusy(true);
    setClearError('');
    try {
      const result = await api.clearOpenCafeOrders(clearPin);
      setClearOpen(false);
      setClearPin('');
      fetchOrders();
      const n = result.collected || 0;
      showToast(n > 0 ? `Marked ${n} order${n === 1 ? '' : 's'} collected` : 'No open orders to clear');
    } catch (err) {
      setClearError(err.message || 'Invalid PIN');
      setClearPin('');
    } finally {
      setClearBusy(false);
    }
  }

  const baseOrders = useMemo(
    () => orders.filter((o) => {
      if (o.status === 'awaiting_payment') return false;
      if (o.status === 'cancelled' && o.payment_method === 'card') return false;
      return true;
    }),
    [orders]
  );

  const { dueOrders, scheduledOrders } = useMemo(() => {
    const due = [];
    const sched = [];
    for (const o of baseOrders) {
      if (isPickupLaterToday(o)) sched.push(o);
      else due.push(o);
    }
    return { dueOrders: due, scheduledOrders: sched };
  }, [baseOrders, timeTick]);

  const allSectionsRaw = useMemo(() => groupOrdersBySection(dueOrders), [dueOrders]);
  const scheduledSectionsRaw = useMemo(() => groupOrdersBySection(scheduledOrders), [scheduledOrders]);

  const applySectionFilters = useCallback(
    (sectionList) =>
      sectionList.map((section) => ({
        ...section,
        orders: section.orders.filter((o) => {
          if (filter === 'active' && !['pending', 'preparing', 'ready'].includes(o.sectionStatus)) return false;
          if (
            filter === 'completed' &&
            !(['collected', 'cancelled'].includes(o.sectionStatus) || o.status === 'cancelled')
          )
            return false;
          if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            return (o.customer_name || '').toLowerCase().includes(q);
          }
          return true;
        }),
      })),
    [filter, searchQuery]
  );

  const filteredDue = useMemo(() => applySectionFilters(allSectionsRaw), [allSectionsRaw, applySectionFilters]);
  const filteredScheduled = useMemo(
    () => applySectionFilters(scheduledSectionsRaw),
    [scheduledSectionsRaw, applySectionFilters]
  );

  const extraTabNames = useMemo(() => {
    const names = new Set(['All', 'Scheduled', ...SECTION_ORDER]);
    const extras = [];
    for (const s of [...allSectionsRaw, ...scheduledSectionsRaw]) {
      if (!names.has(s.name)) {
        names.add(s.name);
        extras.push(s.name);
      }
    }
    return extras;
  }, [allSectionsRaw, scheduledSectionsRaw]);

  const tabNames = useMemo(
    () => ['All', 'Scheduled', ...SECTION_ORDER, ...extraTabNames],
    [extraTabNames]
  );

  useEffect(() => {
    const valid = selectedTabs.filter((t) => tabNames.includes(t));
    if (valid.length === 0) {
      setSelectedTabs(['All']);
    } else if (valid.length !== selectedTabs.length) {
      setSelectedTabs(valid);
    }
  }, [tabNames, selectedTabs]);

  useEffect(() => {
    sessionStorage.setItem('cafe-tabs', JSON.stringify(selectedTabs));
  }, [selectedTabs]);

  const scheduledAlertCount = useMemo(
    () => countActiveScheduledOrders(scheduledSectionsRaw),
    [scheduledSectionsRaw, timeTick]
  );

  const sectionsForView = useMemo(() => {
    const hasAll = selectedTabs.includes('All');
    const hasScheduled = selectedTabs.includes('Scheduled');
    const categoryTabs = selectedTabs.filter((t) => t !== 'All' && t !== 'Scheduled');

    if (hasAll) {
      const sched = filteredScheduled.filter((s) => s.orders.length > 0);
      const due = filteredDue.filter((s) => s.orders.length > 0);
      return { mode: 'all', scheduled: sched, due };
    }

    const showScheduled = hasScheduled;
    const scheduled = showScheduled ? filteredScheduled.filter((s) => s.orders.length > 0) : [];

    if (categoryTabs.length === 0 && showScheduled) {
      return { mode: 'scheduled', scheduled: filteredScheduled };
    }

    const due = categoryTabs.length > 0
      ? filteredDue.filter((s) => categoryTabs.includes(s.name) && s.orders.length > 0)
      : [];

    if (categoryTabs.length === 1 && !showScheduled) {
      const sec = filteredDue.find((s) => s.name === categoryTabs[0]);
      return { mode: 'category', category: sec || { name: categoryTabs[0], orders: [] } };
    }

    return { mode: 'multi', scheduled, due };
  }, [selectedTabs, filteredDue, filteredScheduled]);

  const visibleOrderCount = useMemo(() => {
    if (sectionsForView.mode === 'all' || sectionsForView.mode === 'multi') {
      return (
        (sectionsForView.scheduled || []).reduce((n, s) => n + s.orders.length, 0) +
        (sectionsForView.due || []).reduce((n, s) => n + s.orders.length, 0)
      );
    }
    if (sectionsForView.mode === 'scheduled') {
      return sectionsForView.scheduled.reduce((n, s) => n + s.orders.length, 0);
    }
    return sectionsForView.category.orders.length;
  }, [sectionsForView]);

  const actionLabel = {
    pending: 'Start Preparing',
    preparing: 'Mark Ready',
    ready: 'Mark Collected',
  };

  const actionClass = {
    pending: 'btn-warning',
    preparing: 'btn-primary',
    ready: 'btn-success',
  };

  const isMultiMode = selectedTabs.length > 1;

  function handleTabTap(tab) {
    if (longPressFiredRef.current) return;
    setSelectedTabs([tab]);
  }

  function handleTabLongPress(tab) {
    setSelectedTabs((prev) => {
      if (tab === 'All') return ['All'];
      const without = prev.filter((t) => t !== 'All');
      if (without.includes(tab)) {
        const next = without.filter((t) => t !== tab);
        return next.length === 0 ? ['All'] : next;
      }
      return [...without, tab];
    });
  }

  function onTabPointerDown(tab) {
    longPressFiredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      handleTabLongPress(tab);
    }, 500);
  }

  function onTabPointerUp() {
    clearTimeout(longPressTimerRef.current);
  }

  function onTabPointerLeave() {
    clearTimeout(longPressTimerRef.current);
  }

  const openOrderCount = useMemo(() => {
    const ids = new Set();
    for (const section of [...allSectionsRaw, ...scheduledSectionsRaw]) {
      for (const order of section.orders) {
        if (['pending', 'preparing', 'ready'].includes(order.sectionStatus)) ids.add(order.id);
      }
    }
    return ids.size;
  }, [allSectionsRaw, scheduledSectionsRaw]);

  const activeCount = useMemo(() => {
    let count = 0;
    for (const section of allSectionsRaw) {
      count += section.orders.filter((o) => ['pending', 'preparing', 'ready'].includes(o.sectionStatus)).length;
    }
    for (const section of scheduledSectionsRaw) {
      count += section.orders.filter((o) => ['pending', 'preparing', 'ready'].includes(o.sectionStatus)).length;
    }
    return count;
  }, [allSectionsRaw, scheduledSectionsRaw, timeTick]);

  if (loading) {
    return (
      <div className="page-center">
        <div className="spinner" />
        <p>Loading orders...</p>
      </div>
    );
  }

  return (
    <main className="cafe-dashboard">
      <div className="cafe-watermark-wrap" aria-hidden="true"><img src="/logo.png" alt="" /></div>
      {idleWarning && (
        <div className="idle-warning">
          Logging out in 1 minute due to inactivity — tap anywhere to stay logged in
        </div>
      )}
      <div className="cafe-header">
        <div className="cafe-header-title">
          <h1>Holy Ground Cafe</h1>
          {activeCount > 0 && (
            <span className="cafe-active-count">{activeCount} active</span>
          )}
        </div>
        <button
          className="btn btn-sm btn-outline logout-btn"
          onClick={() => {
            sessionStorage.removeItem('pin-auth-cafe');
            sessionStorage.removeItem('auth-token');
            navigate('/');
          }}
        >
          Logout
        </button>
        <div className="cafe-filters">
          {['active', 'completed', 'all'].map((f) => (
            <button
              key={f}
              className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="cafe-toolbar">
        <div className="cafe-print-menu" ref={printMenuRef}>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={printBusy}
            aria-expanded={printMenuOpen}
            onClick={() => setPrintMenuOpen((open) => !open)}
          >
            {printBusy && !printPreview ? 'Checking…' : 'Print'}
          </button>
          {printMenuOpen && (
            <div className="cafe-print-dropdown" role="menu">
              {PRINT_ACTIONS.map((action) => (
                <button
                  key={action.slice}
                  type="button"
                  role="menuitem"
                  onClick={() => choosePrintSlice(action.slice)}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline cafe-clear-btn"
          disabled={openOrderCount === 0 || clearBusy}
          onClick={openClearModal}
        >
          Clear open orders
        </button>
      </div>

      <div className="cafe-tabs">
        {tabNames.map((tab) => {
          const isScheduledTab = tab === 'Scheduled';
          const showAlert = isScheduledTab && scheduledAlertCount > 0;
          const isSelected = selectedTabs.includes(tab);
          const label =
            isScheduledTab && scheduledAlertCount > 0 ? `Scheduled (${scheduledAlertCount})` : tab;
          return (
            <button
              key={tab}
              type="button"
              className={[
                'cafe-tab',
                isSelected ? 'active' : '',
                showAlert ? 'cafe-tab-alert' : '',
                isMultiMode && isSelected ? 'cafe-tab-multi' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => handleTabTap(tab)}
              onPointerDown={() => onTabPointerDown(tab)}
              onPointerUp={onTabPointerUp}
              onPointerLeave={onTabPointerLeave}
              onContextMenu={(e) => e.preventDefault()}
            >
              {isMultiMode && isSelected && <span className="cafe-tab-check">&#10003; </span>}
              {label}
            </button>
          );
        })}
      </div>
      {!isMultiMode && (
        <p className="cafe-tab-hint">Long-press a tab to combine multiple stations</p>
      )}
      {isMultiMode && (
        <p className="cafe-tab-hint">
          Showing {selectedTabs.join(' + ')} &mdash;
          <button type="button" className="btn-link cafe-tab-reset" onClick={() => setSelectedTabs(['All'])}>
            Reset
          </button>
        </p>
      )}

      <div className="cafe-search-bar">
        <input
          type="text"
          className="cafe-search-input"
          placeholder="Search by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button type="button" className="btn btn-sm btn-outline" onClick={() => setSearchQuery('')}>
            Clear
          </button>
        )}
        {searchQuery && (
          <span className="cafe-search-count">
            {visibleOrderCount} result{visibleOrderCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {sectionsForView.mode === 'all' &&
        (sectionsForView.scheduled.length === 0 && sectionsForView.due.length === 0 ? (
          <div className="page-center" style={{ minHeight: '50vh' }}>
            <p className="cafe-empty">{searchQuery ? 'No matching orders' : 'No orders right now'}</p>
          </div>
        ) : (
          <div className="cafe-sections">
            {sectionsForView.scheduled.length > 0 && (
              <div className="cafe-section cafe-scheduled-for-later">
                <h2 className="cafe-section-title">Scheduled for later</h2>
                {sectionsForView.scheduled.map((section) => (
                  <Fragment key={section.name}>
                    <h3 className="cafe-scheduled-category">{section.name}</h3>
                    <div className="cafe-grid">
                      {section.orders.map((order) => (
                        <OrderCard
                          key={`${order.id}-${section.name}`}
                          order={order}
                          section={section}
                          newOrderIds={newOrderIds}
                          advanceSectionStatus={advanceSectionStatus}
                          setCancelTarget={setCancelTarget}
                          actionLabel={actionLabel}
                          actionClass={actionClass}
                          onReactivate={reactivateOrder}
                          onReprintStickers={reprintStickers}
                          reprinting={reprintingId === order.id}
                        />
                      ))}
                    </div>
                  </Fragment>
                ))}
              </div>
            )}
            {sectionsForView.due.map((section) => (
              <div key={section.name} className="cafe-section">
                <h2 className="cafe-section-title">{section.name}</h2>
                <div className="cafe-grid">
                  {section.orders.map((order) => (
                    <OrderCard
                      key={`${order.id}-${section.name}`}
                      order={order}
                      section={section}
                      newOrderIds={newOrderIds}
                      advanceSectionStatus={advanceSectionStatus}
                      setCancelTarget={setCancelTarget}
                      actionLabel={actionLabel}
                      actionClass={actionClass}
                      onReactivate={reactivateOrder}
                      onReprintStickers={reprintStickers}
                      reprinting={reprintingId === order.id}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}

      {sectionsForView.mode === 'scheduled' && (
        <div className="cafe-sections">
          {sectionsForView.scheduled.every((s) => s.orders.length === 0) ? (
            <div className="page-center" style={{ minHeight: '50vh' }}>
              <p className="cafe-empty">{searchQuery ? 'No matching orders' : 'No scheduled orders'}</p>
            </div>
          ) : (
            sectionsForView.scheduled
              .filter((s) => s.orders.length > 0)
              .map((section) => (
                <div key={section.name} className="cafe-section">
                  <h2 className="cafe-section-title">{section.name}</h2>
                  <div className="cafe-grid">
                    {section.orders.map((order) => (
                      <OrderCard
                        key={`${order.id}-${section.name}`}
                        order={order}
                        section={section}
                        newOrderIds={newOrderIds}
                        advanceSectionStatus={advanceSectionStatus}
                        setCancelTarget={setCancelTarget}
                        actionLabel={actionLabel}
                        actionClass={actionClass}
                        onReactivate={reactivateOrder}
                        onReprintStickers={reprintStickers}
                        reprinting={reprintingId === order.id}
                      />
                    ))}
                  </div>
                </div>
              ))
          )}
        </div>
      )}

      {sectionsForView.mode === 'category' && (
        <div className="cafe-sections">
          <div className="cafe-section">
            <h2 className="cafe-section-title">{sectionsForView.category.name}</h2>
            {sectionsForView.category.orders.length === 0 ? (
              <div className="page-center" style={{ minHeight: '40vh' }}>
                <p className="cafe-empty">{searchQuery ? 'No matching orders' : 'No orders in this tab'}</p>
              </div>
            ) : (
              <div className="cafe-grid">
                {sectionsForView.category.orders.map((order) => (
                  <OrderCard
                    key={`${order.id}-${sectionsForView.category.name}`}
                    order={order}
                    section={sectionsForView.category}
                    newOrderIds={newOrderIds}
                    advanceSectionStatus={advanceSectionStatus}
                    setCancelTarget={setCancelTarget}
                    actionLabel={actionLabel}
                    actionClass={actionClass}
                    onReactivate={reactivateOrder}
                    onReprintStickers={reprintStickers}
                    reprinting={reprintingId === order.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {sectionsForView.mode === 'multi' && (
        (sectionsForView.scheduled.length === 0 && sectionsForView.due.length === 0) ? (
          <div className="page-center" style={{ minHeight: '50vh' }}>
            <p className="cafe-empty">{searchQuery ? 'No matching orders' : 'No orders right now'}</p>
          </div>
        ) : (
          <div className="cafe-sections">
            {sectionsForView.scheduled.length > 0 && (
              <div className="cafe-section cafe-scheduled-for-later">
                <h2 className="cafe-section-title">Scheduled for later</h2>
                {sectionsForView.scheduled.map((section) => (
                  <Fragment key={section.name}>
                    <h3 className="cafe-scheduled-category">{section.name}</h3>
                    <div className="cafe-grid">
                      {section.orders.map((order) => (
                        <OrderCard
                          key={`${order.id}-${section.name}`}
                          order={order}
                          section={section}
                          newOrderIds={newOrderIds}
                          advanceSectionStatus={advanceSectionStatus}
                          setCancelTarget={setCancelTarget}
                          actionLabel={actionLabel}
                          actionClass={actionClass}
                          onReactivate={reactivateOrder}
                          onReprintStickers={reprintStickers}
                          reprinting={reprintingId === order.id}
                        />
                      ))}
                    </div>
                  </Fragment>
                ))}
              </div>
            )}
            {sectionsForView.due.map((section) => (
              <div key={section.name} className="cafe-section">
                <h2 className="cafe-section-title">{section.name}</h2>
                <div className="cafe-grid">
                  {section.orders.map((order) => (
                    <OrderCard
                      key={`${order.id}-${section.name}`}
                      order={order}
                      section={section}
                      newOrderIds={newOrderIds}
                      advanceSectionStatus={advanceSectionStatus}
                      setCancelTarget={setCancelTarget}
                      actionLabel={actionLabel}
                      actionClass={actionClass}
                      onReactivate={reactivateOrder}
                      onReprintStickers={reprintStickers}
                      reprinting={reprintingId === order.id}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {printPreview && (
        <div className="dashboard-modal-overlay" onClick={() => !printBusy && setPrintPreview(null)}>
          <div className="dashboard-modal" onClick={(e) => e.stopPropagation()}>
            {printPreview.stickers > 0 ? (
              <p>
                Queue <strong>{printPreview.stickers}</strong> sticker{printPreview.stickers === 1 ? '' : 's'} for{' '}
                <strong>{printPreview.orders}</strong> open order{printPreview.orders === 1 ? '' : 's'}
                {printPreview.slice === 'all' ? '' : ` (${printPreview.slice})`}?
              </p>
            ) : (
              <p>No open orders need new stickers.</p>
            )}
            {printPreview.replaced > 0 && (
              <p className="cafe-modal-note">
                {printPreview.replaced} sticker{printPreview.replaced === 1 ? '' : 's'} already waiting for this selection will be discarded first, so the printers will not also run an older copy.
              </p>
            )}
            <div className="dashboard-modal-actions">
              <button type="button" className="btn btn-sm btn-primary" disabled={printBusy} onClick={confirmPrint}>
                {printBusy ? 'Queuing…' : (printPreview.stickers > 0 ? 'Print' : 'Discard waiting stickers')}
              </button>
              <button type="button" className="btn btn-sm btn-outline" disabled={printBusy} onClick={() => setPrintPreview(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {clearOpen && (
        <div className="dashboard-modal-overlay" onClick={() => !clearBusy && setClearOpen(false)}>
          <div className="dashboard-modal cafe-clear-modal" onClick={(e) => e.stopPropagation()}>
            <p className="cafe-clear-warning">
              This marks every open cafe order as collected and takes them off the board, including orders scheduled for later. Stickers still waiting for those orders will not print.
            </p>
            <p>
              {openOrderCount} open order{openOrderCount === 1 ? '' : 's'}. Enter the cafe PIN to confirm.
            </p>
            <form onSubmit={confirmClearOpen} className="cafe-clear-form">
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                placeholder="Cafe PIN"
                value={clearPin}
                onChange={(e) => setClearPin(e.target.value.replace(/\D/g, ''))}
                autoFocus
                className="pin-gate-input"
              />
              {clearError && <p className="error-text">{clearError}</p>}
              <div className="dashboard-modal-actions">
                <button type="submit" className="btn btn-sm btn-danger" disabled={clearPin.length < 4 || clearBusy}>
                  {clearBusy ? 'Clearing…' : 'Clear all open orders'}
                </button>
                <button type="button" className="btn btn-sm btn-outline" disabled={clearBusy} onClick={() => setClearOpen(false)}>
                  Keep orders
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {cancelTarget && (
        <div className="dashboard-modal-overlay" onClick={() => setCancelTarget(null)}>
          <div className="dashboard-modal" onClick={(e) => e.stopPropagation()}>
            <p>Cancel order <strong>{cancelTarget.order_number}</strong>?</p>
            <div className="dashboard-modal-actions">
              <button className="btn btn-sm btn-danger" onClick={confirmCancelOrder}>Yes, Cancel</button>
              <button className="btn btn-sm btn-outline" onClick={() => setCancelTarget(null)}>No, Keep</button>
            </div>
          </div>
        </div>
      )}

      {toastMsg && <div className="dashboard-toast">{toastMsg}</div>}
    </main>
  );
}
